import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from '../magazine/MagazinePageHeader';
import { useAuth } from '../../hooks/useAuth';
import { MagazineInvoice, deleteInvoice, getInvoices } from '../../api/invoiceService';
import { qbSyncBadgeClass } from '../../util/qbSyncStatus';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  paid: 'bg-green-50 text-green-700',
  overdue: 'bg-red-50 text-red-700',
};

const sourceSummary = (invoice: MagazineInvoice) => {
  if (invoice.proposal_id && invoice.proposal?.line_items?.length) {
    const count = invoice.proposal.line_items.length;
    const first = invoice.proposal.line_items[0];
    const pub = first.ad_slot?.issue?.publication?.name;
    return `Proposal #${invoice.proposal_id}${pub ? ` · ${pub}` : ''} · ${count} item${count === 1 ? '' : 's'}`;
  }
  const slot = invoice.magazine_booking?.ad_slot;
  const parts = [
    slot?.issue?.publication?.name,
    slot?.issue?.title,
    slot?.label,
  ].filter(Boolean);
  return parts.join(' · ') || '—';
};

const formatMoney = (amount: string | number, currency: string) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
};

const Invoices = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<MagazineInvoice[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const data = await getInvoices(statusFilter || undefined);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const handleDelete = async (id: number) => {
    if (!isAdmin || !window.confirm('Delete this invoice?')) return;
    await deleteInvoice(id);
    load();
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Invoices"
        description="Magazine advertising invoices generated from signed contracts."
      />

      <PageCard>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">
            Status
            <select
              className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Invoice #</th>
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4">Amount</th>
                  {isAdmin && <th className="py-2 pr-4">Sales rep</th>}
                  <th className="py-2 pr-4">Due</th>
                  <th className="py-2 pr-4">Status</th>
                  {isAdmin && <th className="py-2 pr-4">QuickBooks</th>}
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.invoice_number}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.company?.name || '—'}</td>
                    <td className="py-3 pr-4 text-gray-600">{sourceSummary(item)}</td>
                    <td className="py-3 pr-4 text-gray-900">
                      {formatMoney(item.amount, item.currency)}
                    </td>
                    {isAdmin && (
                      <td className="py-3 pr-4 text-gray-600">
                        {item.sales_rep?.username || item.sales_rep?.email || '—'}
                      </td>
                    )}
                    <td className="py-3 pr-4 text-gray-600">
                      {item.due_date ? new Date(item.due_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${statusClass[item.status] || ''}`}>
                        {item.status}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="py-3 pr-4">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${qbSyncBadgeClass(item.qb_sync_status)}`}>
                          {item.qb_sync_status || '—'}
                        </span>
                      </td>
                    )}
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Link to={`/invoices/${item.id}`} className="text-sm text-torch-primary hover:underline">
                          View
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Invoices;
